import React, { useState, useMemo, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from '@mui/material/Popover';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import IconButton from '@mui/material/IconButton';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import SearchIcon from '@mui/icons-material/Search';

import { useSearchParams } from 'react-router-dom';
import { contractAssets, warehouses, stockLevels, parts } from '../../services/dataClient';
import { callClaude } from '../../services/anthropicClient';
import BreadcrumbHeader from '../../components/shared/BreadcrumbHeader';
import FilterTabs from '../../components/shared/FilterTabs';
import StatCard from '../../components/shared/StatCard';
import ConfirmModal from '../../components/shared/ConfirmModal';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ── Constants ─────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";
const SLA_MULTIPLIER = { 4: 2, 8: 1.5, 24: 1 };

const REGION_FALLBACK = {
  'North America - East': ['North America - East', 'North America - Central'],
  'North America - West': ['North America - West', 'North America - Central'],
  'North America - Central': ['North America - Central', 'North America - East', 'North America - West'],
  'Europe - West': ['Europe - West', 'Europe - East'],
  'Europe - East': ['Europe - East', 'Europe - West'],
  'Asia Pacific': ['Asia Pacific'],
  'Middle East & Africa': ['Middle East & Africa', 'Europe - East'],
  'Latin America': ['Latin America', 'North America - Central']
};

const FILTER_TABS = [
{ label: 'All', value: 'all' },
{ label: 'High SLA Risk', value: 'high-sla' },
{ label: 'Shortfall', value: 'shortfall' },
{ label: 'Surplus', value: 'surplus' },
{ label: 'Expiring', value: 'expiring' }];

// ── Helpers ───────────────────────────────────────────────────────────────────

function SlaChip({ hours }) {
  const cfg =
  hours === 4 ? { label: '4hr', bg: '#ffebee', color: '#c62828' } :
  hours === 8 ? { label: '8hr', bg: '#fff3e0', color: '#e65100' } :
  { label: '24hr', bg: 'var(--grey-100)', color: 'var(--grey-600)' };
  return (
    <Chip
      label={cfg.label}
      size="small"
      sx={{ bgcolor: cfg.bg, color: cfg.color, fontSize: '0.75rem', fontWeight: 600, height: 22, borderRadius: '4px' }} />);

}

function GapCell({ gap, size = 'md' }) {
  const fs = size === 'sm' ? '0.8125rem' : '0.875rem';
  if (gap === 0) return <Typography sx={{ fontSize: fs, color: 'var(--grey-400)' }}>—</Typography>;
  if (gap > 0) return <Typography sx={{ fontSize: fs, fontWeight: 600, color: '#d32f2f' }}>+{gap}</Typography>;
  return <Typography sx={{ fontSize: fs, fontWeight: 600, color: '#388e3c' }}>{gap}</Typography>;
}

// ── Part Detail Modal ─────────────────────────────────────────────────────────

function PartDetailModal({
  part,
  acceptedKeys,
  editingKey,
  editForm,
  setEditForm,
  rationaleCache,
  rationaleLoading,
  aiQtyCache,
  aiQtyLoading,
  rationalePopover,
  onRationaleClick,
  onRationaleClose,
  onClose,
  onStartEdit,
  onCancelEdit,
  onAcceptEdit,
  onAcceptDirect
}) {
  if (!part) return null;

  const MOD_TD = {
    fontSize: '0.8125rem',
    color: 'var(--grey-800)',
    borderBottom: '1px solid var(--grey-200)',
    py: 0,
    px: 1.5,
    height: 48,
    bgcolor: '#fff'
  };

  return (
    <>
    <Dialog
        open
        onClose={(_, reason) => {if (reason !== 'backdropClick') onClose();}}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}>
        
      <DialogTitle sx={{ fontWeight: 600,
          fontSize: '1rem',
          color: 'var(--grey-900)',
          pb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2
        }}>
        {/* Part info */}
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: 'var(--grey-900)', fontFamily: 'monospace' }}>
            {part.partNumber}
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', mt: 0.25, fontWeight: 400 }}>
            {part.partDescription}
          </Typography>
        </Box>
        {/* Chips + close */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          <Chip label={part.oem} size="small" sx={{ fontSize: '0.72rem', height: 22, borderRadius: '4px', bgcolor: 'var(--grey-100)', color: 'var(--grey-700)' }} />
          <SlaChip hours={part.worstSlaHours} />
          <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
              
            <HighlightOffIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pb: 2.5, pt: '12px !important' }}>
        <Box sx={{ border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...SUB_TH, pl: 3, minWidth: 180 }}>Location</TableCell>
              <TableCell sx={{ ...SUB_TH, width: 80 }}>Type</TableCell>
              <TableCell sx={{ ...SUB_TH, width: 68, textAlign: 'center' }}>Assets</TableCell>
              <TableCell sx={{ ...SUB_TH, width: 86, textAlign: 'center' }}>Rec. Qty</TableCell>
              <TableCell sx={{ ...SUB_TH, width: 86, textAlign: 'center' }}>Threshold</TableCell>
              <TableCell sx={{ ...SUB_TH, width: 76, textAlign: 'center' }}>Current</TableCell>
              <TableCell sx={{ ...SUB_TH, width: 68, textAlign: 'center' }}>Gap</TableCell>
              <TableCell sx={SUB_TH}>AI Rationale</TableCell>
              <TableCell sx={{ ...SUB_TH, width: 148 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {part.locations.map((loc) => {
                  const isEditing = editingKey === loc.key;
                  const isAccepted = acceptedKeys.has(loc.key);
                  const rationale = rationaleCache[loc.key];
                  const ratLoading = rationaleLoading[loc.key];

                  return (
                    <TableRow key={loc.key} sx={{ '&:hover td': { bgcolor: 'var(--grey-50)' } }}>

                  {/* Location */}
                  <TableCell sx={{ ...MOD_TD, pl: 3 }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--grey-800)' }}>
                      {loc.locationName}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'var(--grey-400)', mt: 0.25 }}>
                      Serving: {loc.assetRegion}
                    </Typography>
                  </TableCell>

                  {/* Type */}
                  <TableCell sx={MOD_TD}>
                    <Chip
                          label={loc.locationType}
                          size="small"
                          sx={{ fontSize: '0.68rem', height: 20, borderRadius: '4px',
                            bgcolor: loc.locationType === 'FSL' ? 'var(--secondary-light)' : 'var(--grey-100)',
                            color: loc.locationType === 'FSL' ? 'var(--secondary-800)' : 'var(--grey-700)'
                          }} />
                        
                  </TableCell>

                  {/* Assets */}
                  <TableCell sx={{ ...MOD_TD, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.8125rem' }}>{loc.assetCount}</Typography>
                  </TableCell>

                  {/* Rec. Qty */}
                  <TableCell sx={{ ...MOD_TD, textAlign: 'center' }}>
                    {isEditing ?
                        <TextField
                          value={editForm.qty}
                          onChange={(e) => setEditForm((p) => ({ ...p, qty: e.target.value }))}
                          size="small" type="number"
                          inputProps={{ min: 1, style: { textAlign: 'center', fontSize: '0.8125rem', padding: '4px 6px' } }}
                          sx={{ width: 66 }} /> :

                        aiQtyLoading[loc.key] ?
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={14} thickness={4} sx={{ color: 'var(--secondary-main)' }} />
                      </Box> :

                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{loc.recommendedQty}</Typography>
                        }
                  </TableCell>

                  {/* Threshold — always shows the live inventory value; updated to = Rec. Qty on Accept */}
                  <TableCell sx={{ ...MOD_TD, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)' }}>{loc.reorderThreshold}</Typography>
                  </TableCell>

                  {/* Current */}
                  <TableCell sx={{ ...MOD_TD, textAlign: 'center' }}>
                    <Typography sx={{ fontSize: '0.8125rem' }}>{loc.currentQty}</Typography>
                  </TableCell>

                  {/* Gap */}
                  <TableCell sx={{ ...MOD_TD, textAlign: 'center' }}>
                    <GapCell gap={loc.gap} size="sm" />
                  </TableCell>

                  {/* AI Rationale */}
                  <TableCell sx={{ ...MOD_TD, minWidth: 180 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                            sx={{ fontSize: '0.75rem', color: 'var(--grey-400)', fontStyle: 'italic', flex: 1 }}
                            noWrap>
                            
                        {rationale ?
                            rationale.slice(0, 55) + (rationale.length > 55 ? '…' : '') :
                            `${loc.assetCount} × ${SLA_MULTIPLIER[loc.slaHours] ?? 1}× SLA`}
                      </Typography>
                      <IconButton
                            size="small"
                            onClick={(e) => onRationaleClick(e, loc)}
                            sx={{ flexShrink: 0,
                              color: ratLoading ? 'var(--secondary-main)' : 'var(--grey-400)',
                              '&:hover': { color: 'var(--secondary-main)' }
                            }}>
                            
                        {ratLoading ?
                            <CircularProgress size={13} thickness={4} sx={{ color: 'inherit' }} /> :
                            <AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                      </IconButton>
                    </Box>
                  </TableCell>

                  {/* Actions */}
                  <TableCell sx={{ ...MOD_TD, pr: 2 }} onClick={(e) => e.stopPropagation()}>
                    {isEditing ?
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Button
                            size="small" variant="contained"
                            onClick={() => onAcceptEdit(loc)}
                            sx={{ fontSize: '0.75rem', height: 26, px: 1.25, minWidth: 0, bgcolor: '#388e3c', '&:hover': { bgcolor: '#2e7d32' } }}>
                            
                          Accept
                        </Button>
                        <Typography onClick={onCancelEdit} sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-800)' } }}>
                          Cancel
                        </Typography>
                      </Box> :
                        isAccepted ?
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: 14, color: '#388e3c' }} />
                        <Typography sx={{ fontSize: '0.75rem', color: '#388e3c', fontWeight: 500 }}>Accepted</Typography>
                        <Typography onClick={() => onStartEdit(loc)} sx={{ fontSize: '0.72rem', color: 'var(--grey-400)', cursor: 'pointer', ml: 0.5, '&:hover': { color: 'var(--secondary-main)' } }}>
                          Edit
                        </Typography>
                      </Box> :

                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Typography onClick={() => onStartEdit(loc)} sx={{ fontSize: '0.75rem', color: 'var(--secondary-main)', cursor: 'pointer', fontWeight: 500 }}>Edit</Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-300)' }}>|</Typography>
                        <Typography onClick={() => onAcceptDirect(loc)} sx={{ fontSize: '0.75rem', color: '#388e3c', cursor: 'pointer', fontWeight: 500 }}>Accept</Typography>
                      </Box>
                        }
                  </TableCell>

                </TableRow>);

                })}
          </TableBody>
        </Table>
        </Box>
      </DialogContent>

    </Dialog>

    {/* Rationale popover — click-triggered, not auto-fetched */}
    <Popover
        open={Boolean(rationalePopover)}
        anchorEl={rationalePopover?.anchor}
        onClose={onRationaleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            maxWidth: 360,
            p: 2,
            borderRadius: '8px',
            border: '1px solid var(--grey-200)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
          }
        }}>
        
      {rationalePopover &&
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
            <AutoAwesomeIcon sx={{ fontSize: 14, color: 'var(--secondary-main)' }} />
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--grey-700)' }}>
              AI Rationale
            </Typography>
          </Box>
          {rationaleLoading[rationalePopover.key] ?
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={12} thickness={4} sx={{ color: 'var(--secondary-main)' }} />
              <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-400)' }}>Generating…</Typography>
            </Box> :
          rationaleCache[rationalePopover.key] ?
          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-800)', lineHeight: 1.6 }}>
              {rationaleCache[rationalePopover.key]}
            </Typography> :

          <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-500)', fontStyle: 'italic' }}>
              No rationale available.
            </Typography>
          }
        </Box>
        }
    </Popover>
    </>);

}

// ── Table styles ──────────────────────────────────────────────────────────────

const TH = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--grey-700)',
  bgcolor: '#eef2f6',
  borderBottom: '1px solid var(--grey-200)',
  py: '12px',
  px: 2,
  whiteSpace: 'nowrap'
};

const TD = {
  fontSize: '0.875rem',
  color: 'var(--grey-800)',
  borderBottom: '1px solid var(--grey-200)',
  py: 0,
  px: 2,
  height: 48
};

const SUB_TH = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--grey-700)',
  bgcolor: '#eef2f6',
  borderBottom: '1px solid var(--grey-200)',
  py: '12px',
  px: 1.5
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function SmartStockingPage() {
  const [searchParams] = useSearchParams();
  const autoOpenedRef = useRef(false);

  const [activeTab, setActiveTab] = useState('all');
  const [selectedPartNumber, setSelectedPartNumber] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editForm, setEditForm] = useState({ qty: '' });
  const [acceptedKeys, setAcceptedKeys] = useState(new Set());
  const [localOverrides, setLocalOverrides] = useState({});
  const [search, setSearch] = useState('');

  // ── AI state ──────────────────────────────────────────────────────────────
  const [forecastText, setForecastText] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [rationaleCache, setRationaleCache] = useState({});
  const [rationaleLoading, setRationaleLoading] = useState({});
  const [aiQtyCache, setAiQtyCache] = useState({}); // { [locKey]: { qty, threshold } }
  const [aiQtyLoading, setAiQtyLoading] = useState({}); // { [locKey]: boolean }
  const [rationalePopover, setRationalePopover] = useState(null); // null | { key, anchor }
  const [thresholdVersion, setThresholdVersion] = useState(0); // increments to force recommendations recompute after threshold save
  const [confirmAcceptAll, setConfirmAcceptAll] = useState(false);

  // ── Recommendation Engine ─────────────────────────────────────────────────

  const recommendations = useMemo(() => {
    // Build active-warehouse region map (FSL preferred over Warehouse)
    const warehousesByRegion = {};
    warehouses
    .filter((w) => w.active && w.type !== 'On-Site')
    .forEach((w) => {
      if (!warehousesByRegion[w.region]) warehousesByRegion[w.region] = [];
      warehousesByRegion[w.region].push(w);
    });
    Object.values(warehousesByRegion).forEach((arr) =>
    arr.sort((a, b) => a.type === 'FSL' ? -1 : b.type === 'FSL' ? 1 : 0)
    );

    // Group assets by (partNumber, assetRegion) — exclude Expired
    const groupMap = {};
    contractAssets
    .filter((a) => a.status !== 'Expired')
    .forEach((asset) => {
      const key = `${asset.partNumber}|${asset.customerAddress.region}`;
      if (!groupMap[key]) {
        groupMap[key] = {
          partNumber: asset.partNumber,
          partDescription: asset.partDescription,
          oem: asset.oem,
          region: asset.customerAddress.region,
          assetCount: 0,
          minSlaHours: Infinity,
          hasExpiring: false
        };
      }
      const g = groupMap[key];
      g.assetCount++;
      g.minSlaHours = Math.min(g.minSlaHours, asset.slaHours);
      if (asset.status === 'Expiring') g.hasExpiring = true;
    });

    // Resolve target warehouse per group; merge groups mapping to the same (part, location)
    const locationAcc = {};
    Object.values(groupMap).forEach((group) => {
      const chain = REGION_FALLBACK[group.region] || [group.region];
      let target = null;
      for (const fbRegion of chain) {
        const candidates = warehousesByRegion[fbRegion];
        if (candidates && candidates.length > 0) {target = candidates[0];break;}
      }
      if (!target) return;

      const key = `${group.partNumber}|${target.name}`;
      if (!locationAcc[key]) {
        locationAcc[key] = {
          partNumber: group.partNumber,
          partDescription: group.partDescription,
          oem: group.oem,
          locationName: target.name,
          locationType: target.type,
          locationRegion: target.region,
          assetCount: 0,
          minSlaHours: Infinity,
          hasExpiring: false,
          assetRegions: []
        };
      }
      const acc = locationAcc[key];
      acc.assetCount += group.assetCount;
      acc.minSlaHours = Math.min(acc.minSlaHours, group.minSlaHours);
      if (group.hasExpiring) acc.hasExpiring = true;
      if (!acc.assetRegions.includes(group.region)) acc.assetRegions.push(group.region);
    });

    // Convert accumulated groups → location rows with computed quantities
    const locationRows = Object.entries(locationAcc).map(([key, acc]) => {
      const multiplier = SLA_MULTIPLIER[acc.minSlaHours] ?? 1;
      const baseQty = Math.ceil(acc.assetCount * multiplier);
      const override = localOverrides[key];
      const aiQty = aiQtyCache[key];
      const recommendedQty = override?.qty ?? aiQty?.qty ?? baseQty;
      const sl = stockLevels.find(
        (s) => s.partNumber === acc.partNumber && s.locationName === acc.locationName
      );
      const currentQty = sl ? sl.quantityOnHand : 0;
      // Threshold comes from the live inventory record, updated on Accept
      const reorderThreshold = sl ? sl.reorderThreshold ?? 0 : 0;

      return {
        key,
        partNumber: acc.partNumber,
        partDescription: acc.partDescription,
        oem: acc.oem,
        assetRegion: acc.assetRegions.length === 1 ? acc.assetRegions[0] : acc.assetRegions.join(', '),
        locationName: acc.locationName,
        locationType: acc.locationType,
        locationRegion: acc.locationRegion,
        assetCount: acc.assetCount,
        slaHours: acc.minSlaHours,
        recommendedQty,
        reorderThreshold,
        currentQty,
        gap: recommendedQty - currentQty,
        hasExpiring: acc.hasExpiring,
        stockLevelId: sl ? sl.id : null
      };
    });

    // Aggregate location rows → parent rows keyed by partNumber
    const partMap = {};
    locationRows.forEach((row) => {
      if (!partMap[row.partNumber]) {
        const part = parts.find((p) => p.partNumber === row.partNumber);
        partMap[row.partNumber] = {
          partNumber: row.partNumber,
          partDescription: row.partDescription,
          oem: row.oem,
          partId: part ? part.id : null,
          totalAssets: 0,
          worstSlaHours: Infinity,
          totalRecommended: 0,
          totalCurrent: 0,
          totalGap: 0,
          hasExpiring: false,
          locations: []
        };
      }
      const entry = partMap[row.partNumber];
      entry.totalAssets += row.assetCount;
      entry.worstSlaHours = Math.min(entry.worstSlaHours, row.slaHours);
      entry.totalRecommended += row.recommendedQty;
      entry.totalCurrent += row.currentQty;
      entry.totalGap += row.gap;
      if (row.hasExpiring) entry.hasExpiring = true;
      entry.locations.push(row);
    });

    return Object.values(partMap).sort(
      (a, b) => a.worstSlaHours - b.worstSlaHours || b.totalGap - a.totalGap
    );
    // thresholdVersion is a manual refresh trigger — including it on purpose so the memo
    // recomputes when stockLevels entries are mutated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localOverrides, aiQtyCache, thresholdVersion]);

  // Always derives from the live recommendations array so the modal sees fresh values after accepts
  const selectedPart = useMemo(
    () => selectedPartNumber ? recommendations.find((r) => r.partNumber === selectedPartNumber) ?? null : null,
    [recommendations, selectedPartNumber]
  );

  // ── Derived stats ─────────────────────────────────────────────────────────

  const stats = useMemo(() => ({
    totalAssets: recommendations.reduce((s, r) => s + r.totalAssets, 0),
    totalLocations: recommendations.reduce((s, r) => s + r.locations.length, 0),
    shortfalls: recommendations.filter((r) => r.totalGap > 0).length,
    highSla: recommendations.filter((r) => r.worstSlaHours === 4).length
  }), [recommendations]);

  const filteredRows = useMemo(() => {
    let rows;
    switch (activeTab) {
      case 'high-sla':rows = recommendations.filter((r) => r.worstSlaHours === 4);break;
      case 'shortfall':rows = recommendations.filter((r) => r.totalGap > 0);break;
      case 'surplus':rows = recommendations.filter((r) => r.totalGap < 0);break;
      case 'expiring':rows = recommendations.filter((r) => r.hasExpiring);break;
      default:rows = recommendations;
    }
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) =>
      r.partNumber.toLowerCase().includes(q) ||
      r.partDescription.toLowerCase().includes(q) ||
      (r.oem && r.oem.toLowerCase().includes(q))
    );
  }, [recommendations, activeTab, search]);

  // ── AI handlers ───────────────────────────────────────────────────────────

  const handleGenerateForecast = async () => {
    setForecastLoading(true);
    try {
      const summaryLines = recommendations.map((part) => {
        const locLines = part.locations
        .map((l) =>
        `    • ${l.locationName} (${l.locationRegion}): ${l.assetCount} assets, ` +
        `SLA ${l.slaHours}hr, rec ${l.recommendedQty}, current ${l.currentQty}, ` +
        `gap ${l.gap >= 0 ? '+' : ''}${l.gap}`
        )
        .join('\n');
        return (
          `${part.partNumber} (${part.oem}) — ${part.totalAssets} assets, ` +
          `worst SLA ${part.worstSlaHours}hr, total gap ${part.totalGap >= 0 ? '+' : ''}${part.totalGap}` +
          `${part.hasExpiring ? ', has expiring contracts' : ''}\n${locLines}`);

      }).join('\n\n');

      const prompt =
      `You are a supply chain analyst for a managed IT services company. ` +
      `Based on the contracted asset data and stocking recommendations below, ` +
      `write a 3–5 sentence executive summary covering: the most critical parts ` +
      `to stock urgently, the highest-risk locations by SLA tier, and the most ` +
      `pressing coverage gaps. Be direct and specific — name actual part numbers ` +
      `and locations. Do not use bullet points; write in plain prose.\n\n` +
      `Stocking recommendations:\n${summaryLines}`;

      const text = await callClaude({ model: 'claude-sonnet-4-6', maxTokens: 1000, prompt });
      setForecastText(text);
    } catch (err) {
      setForecastText(`Unable to generate forecast: ${err.message}`);
    }
    setForecastLoading(false);
  };

  const fetchRationale = async (loc) => {
    // Guard: skip if already cached or in-flight
    if (rationaleCache[loc.key] || rationaleLoading[loc.key]) return;

    setRationaleLoading((prev) => ({ ...prev, [loc.key]: true }));
    try {
      const gapStr = loc.gap >= 0 ? `+${loc.gap} (shortfall)` : `${loc.gap} (surplus)`;
      const prompt =
      `You are a supply chain analyst for a managed IT services company. ` +
      `In 2–3 plain-English sentences, explain why ${loc.partNumber} (${loc.oem}) ` +
      `needs to be stocked at ${loc.locationName} in this quantity. ` +
      `Be specific about the SLA requirement, the number of contracted assets ` +
      `depending on this location, and the urgency of closing the coverage gap. ` +
      `Do not use bullet points.\n\n` +
      `Context:\n` +
      `- Part: ${loc.partNumber} | OEM: ${loc.oem}\n` +
      `- SLA requirement: ${loc.slaHours}-hour response\n` +
      `- Location: ${loc.locationName} (${loc.locationRegion})\n` +
      `- Contracted assets served by this location: ${loc.assetCount}\n` +
      `- Recommended stock quantity: ${loc.recommendedQty}\n` +
      `- Reorder threshold: ${loc.reorderThreshold}\n` +
      `- Current stock on hand: ${loc.currentQty}\n` +
      `- Gap: ${gapStr}`;

      const text = await callClaude({ model: 'claude-sonnet-4-6', maxTokens: 500, prompt });
      setRationaleCache((prev) => ({ ...prev, [loc.key]: text }));
    } catch (err) {
      setRationaleCache((prev) => ({
        ...prev,
        [loc.key]: `Unable to generate rationale: ${err.message}`
      }));
    }
    setRationaleLoading((prev) => ({ ...prev, [loc.key]: false }));
  };

  const fetchAiQty = async (loc) => {
    if (aiQtyCache[loc.key] || aiQtyLoading[loc.key] || localOverrides[loc.key]) return;

    // Compute formula baseline for context and fallback
    const multiplier = SLA_MULTIPLIER[loc.slaHours] ?? 1;
    const baseQty = Math.ceil(loc.assetCount * multiplier);
    const baseThreshold = Math.max(Math.ceil(baseQty * 0.25), 1);

    setAiQtyLoading((prev) => ({ ...prev, [loc.key]: true }));
    try {
      const prompt =
      `You are a supply chain analyst for a managed IT services company.\n` +
      `Recommend a stocking quantity and reorder threshold for the part/location below.\n` +
      `Consider the SLA requirement, asset density, location type, and coverage urgency.\n` +
      `Respond with a 1–2 sentence justification, then output EXACTLY these two lines:\n` +
      `RECOMMENDED_QTY: <integer>\n` +
      `REORDER_THRESHOLD: <integer>\n\n` +
      `- Part: ${loc.partNumber} (${loc.oem})\n` +
      `- SLA: ${loc.slaHours}-hour response\n` +
      `- Location: ${loc.locationName} (${loc.locationRegion}, ${loc.locationType})\n` +
      `- Contracted assets served: ${loc.assetCount}\n` +
      `- Formula baseline: Rec ${baseQty}, Threshold ${baseThreshold}\n` +
      `- Current stock: ${loc.currentQty}`;

      const text = await callClaude({ model: 'claude-sonnet-4-6', maxTokens: 200, prompt });
      const qtyMatch = text.match(/RECOMMENDED_QTY:\s*(\d+)/);
      const thrMatch = text.match(/REORDER_THRESHOLD:\s*(\d+)/);
      setAiQtyCache((prev) => ({
        ...prev,
        [loc.key]: {
          qty: qtyMatch ? Math.max(1, parseInt(qtyMatch[1], 10)) : baseQty,
          threshold: thrMatch ? Math.max(1, parseInt(thrMatch[1], 10)) : baseThreshold
        }
      }));
    } catch {
      // Fall back to formula so spinner never stays stuck
      const multiplier = SLA_MULTIPLIER[loc.slaHours] ?? 1;
      const baseQty = Math.ceil(loc.assetCount * multiplier);
      const baseThreshold = Math.max(Math.ceil(baseQty * 0.25), 1);
      setAiQtyCache((prev) => ({ ...prev, [loc.key]: { qty: baseQty, threshold: baseThreshold } }));
    }
    setAiQtyLoading((prev) => ({ ...prev, [loc.key]: false }));
  };

  // ── Modal open/close ──────────────────────────────────────────────────────

  const handleOpenModal = (part) => {
    setSelectedPartNumber(part.partNumber);
    part.locations.forEach((loc) => fetchAiQty(loc));
  };

  const handleCloseModal = () => {
    setSelectedPartNumber(null);
    setEditingKey(null);
    setEditForm({ qty: '' });
  };

  const handleRationaleClick = (e, loc) => {
    setRationalePopover({ key: loc.key, anchor: e.currentTarget });
    fetchRationale(loc);
  };
  const handleRationaleClose = () => setRationalePopover(null);

  // ── Auto-open modal from ?part= query param (cross-link from Inventory Search) ─
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const partParam = searchParams.get('part');
    if (!partParam || !recommendations.length) return;
    const match = recommendations.find((r) => r.partNumber === partParam);
    if (match) {
      autoOpenedRef.current = true;
      handleOpenModal(match);
    }
  }, [recommendations]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Edit / accept handlers ────────────────────────────────────────────────

  const handleStartEdit = (loc) => {
    setEditingKey(loc.key);
    setEditForm({ qty: String(loc.recommendedQty) });
  };

  const handleCancelEdit = () => {setEditingKey(null);setEditForm({ qty: '' });};

  const applyThresholdToLocation = (partNumber, locationName, threshold, locationType) => {
    let sl = stockLevels.find((s) => s.partNumber === partNumber && s.locationName === locationName);
    if (!sl) {
      // No inventory record yet — create one with 0 on hand so the threshold can be saved
      sl = {
        id: `sl-${partNumber}-${locationName}`.replace(/\s+/g, '-').toLowerCase(),
        partNumber,
        locationName,
        locationType: locationType ?? 'FSL',
        quantityOnHand: 0,
        reorderThreshold: threshold,
        lastUpdated: new Date().toISOString().slice(0, 10)
      };
      stockLevels.push(sl);
    } else {
      sl.reorderThreshold = threshold;
    }
    setThresholdVersion((v) => v + 1);
  };

  const handleAcceptEdit = (loc) => {
    const qty = Math.max(1, parseInt(editForm.qty, 10) || loc.recommendedQty);
    // Threshold is set equal to the accepted Rec. Qty
    setLocalOverrides((prev) => ({ ...prev, [loc.key]: { qty } }));
    setAcceptedKeys((prev) => new Set([...prev, loc.key]));
    setEditingKey(null);
    applyThresholdToLocation(loc.partNumber, loc.locationName, qty, loc.locationType);
  };

  const handleAcceptDirect = (loc) => {
    // Threshold is set equal to the accepted Rec. Qty
    setLocalOverrides((prev) => ({ ...prev, [loc.key]: { qty: loc.recommendedQty } }));
    setAcceptedKeys((prev) => new Set([...prev, loc.key]));
    applyThresholdToLocation(loc.partNumber, loc.locationName, loc.recommendedQty, loc.locationType);
  };

  const handleAcceptAll = () => {
    const newOverrides = {};
    const newAccepted = new Set(acceptedKeys);
    recommendations.forEach((part) =>
    part.locations.forEach((loc) => {
      if (!newAccepted.has(loc.key)) {
        newOverrides[loc.key] = { qty: loc.recommendedQty };
        newAccepted.add(loc.key);
        // Threshold is set equal to the accepted Rec. Qty
        applyThresholdToLocation(loc.partNumber, loc.locationName, loc.recommendedQty, loc.locationType);
      }
    })
    );
    setLocalOverrides((prev) => ({ ...prev, ...newOverrides }));
    setAcceptedKeys(newAccepted);
  };

  // Fires fetchAiQty for every location not yet cached or overridden
  const handleGenerateAllAiQty = () => {
    recommendations.forEach((part) =>
    part.locations.forEach((loc) => fetchAiQty(loc))
    );
  };

  // True while any AI qty call is in-flight
  const isAiQtyGenerating = Object.values(aiQtyLoading).some(Boolean);

  // True once every location has an AI or override value
  const allLocations = recommendations.flatMap((p) => p.locations);
  const aiQtyComplete =
  allLocations.length > 0 &&
  allLocations.every((loc) => aiQtyCache[loc.key] || localOverrides[loc.key]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ pt: 0, px: 'var(--content-padding)', pb: 'var(--content-padding)', minHeight: '100vh', display: 'flex', flexDirection: 'column', gap: 2 }}>

      {/* Page header */}
      <BreadcrumbHeader
        title="Smart Stocking"
        subtitle="Contract-driven stock recommendations to meet SLA commitments by region"
        crumbs={[{ label: 'Inventory', path: '/inventory/search' }, { label: 'Smart Stocking' }]} />

      {/* ── AI Forecast card ── */}
      <Card
        sx={{ border: '1px solid var(--grey-200)',
          borderRadius: '12px',
          boxShadow: 'none',
          bgcolor: '#fff'
        }}>
        
        {/* Header row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2.5, pb: forecastText ? 0 : 2.5 }}>
          <Box
            sx={{ width: 44,
              height: 44,
              borderRadius: '10px',
              bgcolor: 'var(--secondary-light)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
            
            <AutoAwesomeIcon sx={{ fontSize: 22, color: 'var(--secondary-main)' }} />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--grey-900)', mb: 0.25 }}>
              AI Demand Forecast
            </Typography>
            <Typography sx={{ fontSize: '0.8125rem', color: 'var(--grey-600)' }}>
              {forecastText ?
              'Executive summary generated from contract data and stocking recommendations.' :
              'Generate an executive summary of stocking priorities, SLA risk, and coverage gaps across all contracted assets.'}
            </Typography>
          </Box>

          <Button
            variant="contained"
            size="small"
            disabled={forecastLoading}
            onClick={handleGenerateForecast}
            startIcon={
            forecastLoading ?
            <CircularProgress size={13} sx={{ color: 'rgba(255,255,255,0.7)' }} /> :
            <AutoAwesomeIcon sx={{ fontSize: 15 }} />
            }
            sx={{ fontSize: '0.8125rem',
              height: 34,
              minWidth: 158,
              flexShrink: 0,
              bgcolor: 'var(--secondary-main)',
              '&:hover': { bgcolor: 'var(--secondary-800)' },
              '&.Mui-disabled': { bgcolor: 'var(--secondary-main)', opacity: 0.7 }
            }}>
            
            {forecastLoading ? 'Generating…' : forecastText ? 'Regenerate' : 'Generate Forecast'}
          </Button>
        </Box>

        {/* Forecast result */}
        {forecastText &&
        <Box
          sx={{ mx: 2.5,
            mb: 2.5,
            mt: 2,
            p: 2,
            bgcolor: 'var(--grey-50)',
            border: '1px solid var(--grey-200)',
            borderRadius: '8px'
          }}>
          
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)', lineHeight: 1.7 }}>
              {forecastText}
            </Typography>
          </Box>
        }
      </Card>

      {/* Stat cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <StatCard label="Contracted Assets" value={stats.totalAssets} />
        <StatCard label="Stocking Locations" value={stats.totalLocations} />
        <StatCard label="Part Shortfalls" value={stats.shortfalls} />
        <StatCard label="High SLA Risk (4hr)" value={stats.highSla} />
      </Box>

      {/* Table section — Users page style */}
      <ContentCard sx={{ overflow: 'hidden' }}>

        {/* Tabs */}
        <Box sx={{ px: 3, pt: 2, '& > div': { mb: 0 } }}>
          <FilterTabs tabs={FILTER_TABS} value={activeTab} onChange={setActiveTab} />
        </Box>

        {/* Content area */}
        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Search + buttons row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
            <TextField
              size="small"
              placeholder="Search by Part #, Description, or OEM…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment:
                <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                  </InputAdornment>

              }}
              sx={{ minWidth: 400, maxWidth: 600, backgroundColor: 'var(--paper)', borderRadius: 1, ...fieldSx }} />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FileDownloadIcon sx={{ fontSize: 15 }} />}
                sx={{ fontSize: '0.8125rem',
                  height: 34,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  borderColor: 'var(--grey-300)',
                  color: 'var(--grey-700)',
                  '&:hover': { borderColor: 'var(--grey-500)', bgcolor: 'transparent' }
                }}>
                
                Export
              </Button>
              <Button
                variant="outlined"
                size="small"
                disabled={isAiQtyGenerating}
                onClick={handleGenerateAllAiQty}
                startIcon={
                isAiQtyGenerating ?
                <CircularProgress size={13} sx={{ color: 'var(--secondary-main)' }} /> :
                <AutoAwesomeIcon sx={{ fontSize: 15 }} />
                }
                sx={{ fontSize: '0.8125rem',
                  height: 34,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  borderColor: aiQtyComplete ? 'var(--secondary-main)' : 'var(--grey-300)',
                  color: aiQtyComplete ? 'var(--secondary-main)' : 'var(--grey-700)',
                  '&:hover': { borderColor: 'var(--secondary-main)', color: 'var(--secondary-main)', bgcolor: 'transparent' },
                  '&.Mui-disabled': { borderColor: 'var(--grey-200)', color: 'var(--grey-400)' }
                }}>
                
                {isAiQtyGenerating ? 'Generating…' : aiQtyComplete ? 'AI Rec. Qty ✓' : 'Generate AI Rec. Qty'}
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<DoneAllIcon sx={{ fontSize: 15 }} />}
                onClick={() => setConfirmAcceptAll(true)}
                sx={{ fontSize: '0.8125rem',
                  height: 34,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  backgroundColor: '#009688',
                  '&:hover': { backgroundColor: '#00796B' }
                }}>
                
                Accept All
              </Button>
            </Box>
          </Box>

          {/* Table */}
          {filteredRows.length === 0 ?
          <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>
                No recommendations match the selected filter.
              </Typography>
            </Box> :

          <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...TH, minWidth: 120 }}>Part #</TableCell>
                    <TableCell sx={TH}>Description</TableCell>
                    <TableCell sx={{ ...TH, width: 95 }}>OEM</TableCell>
                    <TableCell sx={{ ...TH, width: 72, textAlign: 'center' }}>Assets</TableCell>
                    <TableCell sx={{ ...TH, width: 82, textAlign: 'center' }}>Total Rec.</TableCell>
                    <TableCell sx={{ ...TH, width: 95, textAlign: 'center' }}>Current Stock</TableCell>
                    <TableCell sx={{ ...TH, width: 72, textAlign: 'center' }}>Gap</TableCell>
                    <TableCell sx={{ ...TH, width: 88, textAlign: 'center' }}>SLA Risk</TableCell>
                    <TableCell sx={{ ...TH, width: 110 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((part) => {
                  const allAccepted = part.locations.length > 0 &&
                  part.locations.every((l) => acceptedKeys.has(l.key));

                  return (
                    <TableRow
                      key={part.partNumber}
                      onClick={() => handleOpenModal(part)}
                      sx={{ cursor: 'pointer', '&:hover td': { bgcolor: 'var(--grey-50)' } }}>
                      
                        <TableCell sx={TD}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--secondary-main)', fontFamily: 'monospace' }}>
                            {part.partNumber}
                          </Typography>
                        </TableCell>

                        <TableCell sx={TD}>
                          <Typography sx={{ fontSize: '0.875rem' }}>{part.partDescription}</Typography>
                        </TableCell>

                        <TableCell sx={TD}>
                          <Chip label={part.oem} size="small" sx={{ fontSize: '0.72rem', height: 22, borderRadius: '4px', bgcolor: 'var(--grey-100)', color: 'var(--grey-700)' }} />
                        </TableCell>

                        <TableCell sx={{ ...TD, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.875rem' }}>{part.totalAssets}</Typography>
                        </TableCell>

                        <TableCell sx={{ ...TD, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{part.totalRecommended}</Typography>
                        </TableCell>

                        <TableCell sx={{ ...TD, textAlign: 'center' }}>
                          <Typography sx={{ fontSize: '0.875rem' }}>{part.totalCurrent}</Typography>
                        </TableCell>

                        <TableCell sx={{ ...TD, textAlign: 'center' }}>
                          <GapCell gap={part.totalGap} />
                        </TableCell>

                        <TableCell sx={{ ...TD, textAlign: 'center' }}>
                          <SlaChip hours={part.worstSlaHours} />
                        </TableCell>

                        <TableCell sx={TD}>
                          {allAccepted &&
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircleIcon sx={{ fontSize: 15, color: '#388e3c' }} />
                              <Typography sx={{ fontSize: '0.8125rem', color: '#388e3c', fontWeight: 500 }}>Accepted</Typography>
                            </Box>
                        }
                        </TableCell>
                      </TableRow>);

                })}
                </TableBody>
              </Table>
            </Box>
          }
        </Box>
      </ContentCard>

      {/* Part detail modal */}
      <PartDetailModal
        part={selectedPart}
        acceptedKeys={acceptedKeys}
        editingKey={editingKey}
        editForm={editForm}
        setEditForm={setEditForm}
        rationaleCache={rationaleCache}
        rationaleLoading={rationaleLoading}
        aiQtyCache={aiQtyCache}
        aiQtyLoading={aiQtyLoading}
        rationalePopover={rationalePopover}
        onRationaleClick={handleRationaleClick}
        onRationaleClose={handleRationaleClose}
        onClose={handleCloseModal}
        onStartEdit={handleStartEdit}
        onCancelEdit={handleCancelEdit}
        onAcceptEdit={handleAcceptEdit}
        onAcceptDirect={handleAcceptDirect} />

      <ConfirmModal
        open={confirmAcceptAll}
        title="Accept All Recommendations"
        message={`This will apply the current recommended quantities across all ${recommendations.reduce((n, r) => n + r.locations.length, 0)} location(s) and set each reorder threshold to match. Rows already accepted individually will be skipped. You can still edit individual rows afterward.`}
        confirmLabel="Accept All"
        onConfirm={() => {setConfirmAcceptAll(false);handleAcceptAll();}}
        onCancel={() => setConfirmAcceptAll(false)} />
      
    </Box>);

}